import {
  type BridgeSeat,
  cardId,
  layOffOnto,
  rummyHint,
  rummyViewFor,
  sameCard,
  sortHand,
  validateMeld,
  wrapFromOf,
} from '@chessmate/game-engine';
import { CARD_WIDTH_CLASS, CardBack, PlayingCard, CARD_SLOT_CLASS, HAND_CLASS } from '../components/PlayingCard.js';
import { OpponentTable } from '../components/table/OpponentTable.js';
import { RoundSummaryPanel } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { pointsSummary } from '../components/table/pointsSummary.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, type PlayerCount, useRummyStore } from '../state/rummy-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

/**
 * The Rummy screen: you in South, robots in the other seats. It renders only from `rummyViewFor(game, HUMAN_SEAT)`, so a hand you
 * may not see is never in the data this component can reach. Rules: draw the top of the stock or of the discard pile, lay down melds
 * (three or four of a kind, or three or more in a row of one suit), add cards to any meld on the table, then discard one card. The
 * first to get rid of every card wins the hand and scores the value of the cards left in the other hands.
 */
export function RummyScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useRummyStore((s) => s.game);
  const result = useRummyStore((s) => s.result);
  const totals = useRummyStore((s) => s.totals);
  const target = useRummyStore((s) => s.target);
  const gameWinner = useRummyStore((s) => s.gameWinner);
  const handNumber = useRummyStore((s) => s.handNumber);
  const seed = useRummyStore((s) => s.seed);
  const players = useRummyStore((s) => s.players);
  const bigCards = useRummyStore((s) => s.bigCards);
  const wrap = useRummyStore((s) => s.wrap);
  const setBigCards = useRummyStore((s) => s.setBigCards);
  const setWrap = useRummyStore((s) => s.setWrap);
  const strength = useRummyStore((s) => s.strength);
  const fourColour = useRummyStore((s) => s.fourColour);
  const assist = useRummyStore((s) => s.assist);
  const selected = useRummyStore((s) => s.selected);
  const toggle = useRummyStore((s) => s.toggle);
  const draw = useRummyStore((s) => s.draw);
  const meld = useRummyStore((s) => s.meld);
  const layOff = useRummyStore((s) => s.layOff);
  const discard = useRummyStore((s) => s.discard);
  const nextHand = useRummyStore((s) => s.nextHand);
  const newGame = useRummyStore((s) => s.newGame);
  const setStrength = useRummyStore((s) => s.setStrength);
  const setFourColour = useRummyStore((s) => s.setFourColour);
  const setAssist = useRummyStore((s) => s.setAssist);
  const setPlayers = useRummyStore((s) => s.setPlayers);

  const identify = useSeatIdentity(['S', 'W', 'N', 'E'], (x) => t(`cardgame-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = rummyViewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`cardgame-seat-${s}`);
  const others = view.seats.filter((s) => s !== HUMAN_SEAT);
  const playing = view.canAct && view.phase === 'play';
  const picked = new Set(selected.map(cardId));
  const canMeld = playing && selected.length >= 3 && validateMeld(selected, view.aceHigh, wrapFromOf(view)) !== null;
  const canDiscard = playing && selected.length === 1 && view.discardable.some((c) => sameCard(c, selected[0] as (typeof selected)[number]));
  const canLayOff = (id: number) => {
    const m = view.melds.find((x) => x.id === id);
    return playing && selected.length === 1 && !!m && layOffOnto(m, selected[0] as (typeof selected)[number], view.aceHigh, wrapFromOf(view)) !== null;
  };

  // The teaching aid: what the clever robot would do now. Off by default, so nothing about the table changes for anyone who has not asked.
  const hint = assist && view.canAct ? rummyHint(view) : null;
  const hinted = new Set<string>();
  if (hint?.type === 'meld') for (const c of hint.cards) hinted.add(cardId(c));
  if (hint?.type === 'layoff' || hint?.type === 'discard') hinted.add(cardId(hint.card));
  const hintMeldId = hint?.type === 'layoff' ? hint.meldId : null;
  const hintText = !hint
    ? ''
    : hint.type === 'draw'
      ? t(hint.from === 'stock' ? 'rummy-hint-draw-stock' : 'rummy-hint-draw-discard')
      : t(`rummy-hint-${hint.type}`);

  let status: string;
  if (view.phase === 'done') {
    status = result
      ? result.blocked
        ? t('rummy-blocked')
        : result.winner === HUMAN_SEAT
          ? t('rummy-hand-won-you', { points: result.points })
          : t('rummy-hand-won', { seat: seatName(result.winner), points: result.points })
      : '';
  } else if (view.canAct) status = view.phase === 'draw' ? t('rummy-turn-draw') : t('rummy-turn-play');
  else status = t('rummy-turn-wait', { seat: seatName(view.turn) });

  const plate = (seat: BridgeSeat) => {
    const id = identify(seat);
    return (
      <SeatPlate
        bubble={bubbles[seat]?.line}
        testid={seat === HUMAN_SEAT ? 'rummy-seat-S' : `rummy-opponent-${seat}`}
        name={id.name}
        tag={id.tag}
        portrait={id.portrait}
        fallbackIcon={id.fallbackIcon}
        you={id.you}
        score={totals[seat] ?? 0}
        scoreLabel={`${seatName(seat)} ${totals[seat] ?? 0}`}
        cardsLeft={id.you ? undefined : (view.handSizes[seat] ?? 0)}
        cardsLeftLabel={t('rummy-cards-left', { n: view.handSizes[seat] ?? 0 })}
        active={view.phase !== 'done' && view.turn === seat}
      />
    );
  };
  const lead = Math.max(...view.seats.map((x) => totals[x] ?? 0));
  const summary = result
    ? pointsSummary({
        round: handNumber,
        seats: view.seats,
        identify,
        totals,
        winner: result.winner,
        points: result.points,
        gameWinner,
        headline: gameWinner ? (gameWinner === HUMAN_SEAT ? t('rummy-game-won') : t('rummy-game-lost', { seat: seatName(gameWinner) })) : status,
      })
    : null;

  const scores = view.seats.map((s) => `${seatName(s)} ${totals[s] ?? 0}`).join(' · ');
  const topCard = view.top;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="rummy-screen" data-appdom-scope="rummy">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('rummy-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="rummy-seed">{t('rummy-deal', { n: handNumber, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="rummy-new-game" data-appdom-id="rummy-new-game" data-appdom-type="Button">{t('rummy-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="rummy-exit" data-appdom-id="rummy-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="rummy-status" data-appdom-id="rummy-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        {hintText && <span className="font-normal text-accent" data-testid="rummy-hint">{hintText}</span>}
        <span className="font-normal text-fg-muted" data-testid="rummy-score">{t('rummy-score', { scores, target })}</span>
      </output>

      <ScoreStrip
        testid="rummy-score-strip"
        title={t('table-score')}
        note={t('table-first-to', { n: target })}
        rows={view.seats.map((x) => ({ key: x, label: identify(x).name, value: totals[x] ?? 0, you: x === HUMAN_SEAT, leading: lead > 0 && (totals[x] ?? 0) === lead }))}
      />

      <OpponentTable
        opponents={others.map((x) => ({ seat: x, node: plate(x) }))}
        center={
          <div className="flex w-full flex-col items-center gap-4 lg:gap-6">
      <div className="flex items-center justify-center gap-6 lg:gap-12">
        <button
          type="button"
          onClick={() => draw('stock')}
          disabled={!view.canDrawStock}
          data-testid="rummy-stock"
          data-appdom-id="rummy-stock"
          data-appdom-type="Button"
          aria-label={`${t('rummy-draw-stock')}. ${t('rummy-stock-left', { n: view.stockCount })}`}
          className={`flex flex-col items-center gap-1 rounded-xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring enabled:hover:-translate-y-0.5 disabled:opacity-60 ${hint?.type === 'draw' && hint.from === 'stock' ? 'ring-2 ring-accent' : ''}`}
        >
          <CardBack className={CARD_WIDTH_CLASS} />
          <span className="text-xs font-bold text-fg-muted">{view.canDrawStock ? t('rummy-draw-stock') : t('rummy-stock-left', { n: view.stockCount })}</span>
        </button>
        <button
          type="button"
          onClick={() => draw('discard')}
          disabled={!view.canDrawDiscard || !topCard}
          data-testid="rummy-discard-pile"
          data-appdom-id="rummy-discard-pile"
          data-appdom-type="Button"
          aria-label={t('rummy-draw-discard')}
          className={`flex flex-col items-center gap-1 rounded-xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring enabled:hover:-translate-y-0.5 disabled:opacity-60 ${hint?.type === 'draw' && hint.from === 'discard' ? 'ring-2 ring-accent' : ''}`}
        >
          {topCard ? <PlayingCard card={topCard} fourColour={fourColour} /> : <span className={CARD_SLOT_CLASS} />}
          <span className="text-xs font-bold text-fg-muted">{view.canDrawDiscard ? t('rummy-draw-discard') : t('rummy-pile')}</span>
        </button>
      </div>

      <section aria-label={t('rummy-melds-title')} data-testid="rummy-melds" className="flex flex-col gap-2">
        <h2 className="text-sm font-bold text-fg-muted">{t('rummy-melds-title')}</h2>
        {view.melds.length === 0 && <p className="text-sm text-fg-muted" data-testid="rummy-no-melds">{t('rummy-no-melds')}</p>}
        <div className="flex flex-wrap gap-3">
          {view.melds.map((m) => (
            <div
              key={m.id}
              data-testid={`rummy-meld-${m.id}`}
              data-kind={m.kind}
              className={`flex flex-col items-start gap-1 rounded-xl border bg-surface p-2 ${hintMeldId === m.id ? 'border-accent ring-2 ring-accent' : 'border-border'}`}
            >
              <span className="text-xs font-bold text-fg-muted">{t('rummy-meld-of', { seat: seatName(m.owner), kind: t(`rummy-kind-${m.kind}`) })}</span>
              <div className="flex gap-1">
                {m.cards.map((c) => (
                  <PlayingCard key={cardId(c)} card={c} fourColour={fourColour} />
                ))}
              </div>
              {playing && (
                <button
                  type="button"
                  disabled={!canLayOff(m.id)}
                  onClick={() => layOff(m.id)}
                  data-testid={`rummy-layoff-${m.id}`}
                  className="min-h-[48px] rounded-lg border border-border px-2 py-1 text-xs font-bold enabled:hover:bg-surface-raised disabled:opacity-50"
                >
                  {t('rummy-layoff')}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {playing && (
        <div className="flex flex-wrap items-center justify-center gap-2" data-testid="rummy-actions">
          <button type="button" onClick={meld} disabled={!canMeld} data-testid="rummy-meld-btn" data-appdom-id="rummy-meld-btn" data-appdom-type="Button" className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg disabled:opacity-50">
            {t('rummy-meld')}
          </button>
          <button type="button" onClick={discard} disabled={!canDiscard} data-testid="rummy-discard-btn" data-appdom-id="rummy-discard-btn" data-appdom-type="Button" className="min-h-[48px] rounded-xl border border-border px-4 py-2 font-display font-semibold disabled:opacity-50">
            {t('rummy-discard')}
          </button>
          <span className="text-sm text-fg-muted" data-testid="rummy-picked">{t('rummy-picked', { n: selected.length })}</span>
        </div>
      )}

          </div>
        }
        bottom={
      <section aria-label={t('bridge-hand-of', { seat: seatName(HUMAN_SEAT) })} data-testid="rummy-hand" className="flex flex-col items-center gap-3">
        {plate(HUMAN_SEAT)}
        <div className={HAND_CLASS}>
          {sortHand([...view.hand]).map((c) => (
            <PlayingCard
              key={cardId(c)}
              card={c}
              fourColour={fourColour}
              onPlay={playing ? () => toggle(c) : undefined}
              selected={playing ? picked.has(cardId(c)) : undefined}
              highlight={hinted.has(cardId(c))}
            />
          ))}
        </div>
      </section>

        }
      />

      {summary && (
        <RoundSummaryPanel
          testid="rummy-result"
          headlineTestid="rummy-game-over"
          summary={summary}
          nextLabel={gameWinner ? t('rummy-new-game') : t('rummy-next-hand')}
          onNext={gameWinner ? newGame : nextHand}
          nextTestid={gameWinner ? 'rummy-play-again' : 'rummy-next-hand'}
        />
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="rummy-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`rummy-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <div className="flex flex-col gap-1">
            <span>{t('rummy-players')}</span>
            <div className="flex items-center gap-2">
              {([2, 3, 4] as PlayerCount[]).map((n) => (
                <button key={n} type="button" aria-pressed={players === n} data-testid={`rummy-players-${n}`} onClick={() => setPlayers(n)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${players === n ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                  {t('rummy-players-n', { n })}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={bigCards} onChange={(e) => setBigCards(e.target.checked)} data-testid="rummy-big-cards" />
            {t('big-cards')}
          </label>
          {bigCards && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={wrap ?? true} onChange={(e) => setWrap(e.target.checked)} data-testid="rummy-wrap" />
              {t('rummy-wrap')}
            </label>
          )}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={assist} onChange={(e) => setAssist(e.target.checked)} data-testid="rummy-assist" />
            {t('rummy-assist')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="rummy-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <p className="font-normal text-fg-muted">{t('rummy-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}
